import React, { useEffect, useState, useCallback, useMemo } from "react";
import { 
  Card, 
  Table, 
  Tag, 
  Select, 
  Spin, 
  Button, 
  Popconfirm, 
  message, 
  Space, 
  Modal, 
  Tooltip,
  Row,
  Col,
  Input
} from "antd";
import { 
  DownloadOutlined, 
  EyeOutlined, 
  FilePdfOutlined, 
  FileExcelOutlined, 
  FileOutlined,
  SearchOutlined
} from "@ant-design/icons";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import useExpenseManagement from "Hooks/EmployeeHooks/useExpenseManagement";
import {
  ExpenseManagemnetStatusEnum,
  ExpenseManagemnetStatusLabel,
} from "Constants/Enums/expenseManagmentEnum";

const { Option } = Select;
const { Search } = Input;

const ExpenseManagementList = () => {
  const { expenseList, loading, fetchExpenseList, updateExpenseStatus } = useExpenseManagement();
  const [filteredStatus, setFilteredStatus] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewType, setPreviewType] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);
  const BASE_URL = process.env.REACT_APP_Base_URL;

  // Memoize filtered list to prevent infinite re-renders
  const filteredList = useMemo(() => {
    
    if (!Array.isArray(expenseList)) {
      return [];
    }

    let filtered = [...expenseList];

    // Apply search filter
    if (searchText && searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      filtered = filtered.filter((item) =>
        (item.description && item.description.toLowerCase().includes(searchLower)) ||
        (item.employee && item.employee.fullName && item.employee.fullName.toLowerCase().includes(searchLower)) ||
        (item.department && item.department.name && item.department.name.toLowerCase().includes(searchLower)) ||
        (item.approver && item.approver.fullName && item.approver.fullName.toLowerCase().includes(searchLower))
      );
    }

    // Apply status filter
    if (filteredStatus !== "all") {
      const statusNum = parseInt(filteredStatus);
      if (!isNaN(statusNum)) {
        filtered = filtered.filter((item) => item.status === statusNum);
      }
    }

    return filtered;
  }, [expenseList, filteredStatus, searchText]);

  // Memoize columns to prevent infinite re-renders
  const columns = useMemo(() => [
    {
      title: "Employee Name",
      dataIndex: ["employee", "fullName"],
      key: "employee",
      render: (fullName, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden"
          }}>
            {record.employee?.profileImage ? (
              <img 
                src={`${BASE_URL}${record.employee.profileImage}`}
                alt={fullName}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <span style={{ fontSize: "14px", color: "#666" }}>
                {record.employee?.firstName?.charAt(0) || "E"}
              </span>
            )}
          </div>
          <span style={{ fontSize: "14px", color: "#333" }}>
            {fullName || "—"}
          </span>
        </div>
      ),
    },
    {
      title: "Department",
      dataIndex: ["department", "name"],
      key: "department",
      render: (name) => (
        <span style={{ fontSize: "14px", color: "#333" }}>
          {name || "—"}
        </span>
      ),
    },
    {
      title: "Approver",
      dataIndex: ["approver", "fullName"],
      key: "approver",
      render: (fullName, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            backgroundColor: "#e6f7ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden"
          }}>
            {record.approver?.profileImage ? (
              <img 
                src={`${BASE_URL}${record.approver.profileImage}`}
                alt={fullName}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <span style={{ fontSize: "12px", color: "#666" }}>
                {record.approver?.firstName?.charAt(0) || "J"}
              </span>
            )}
          </div>
          <span style={{ fontSize: "14px", color: "#333" }}>
            {fullName || "—"}
          </span>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (dateString) => {
        if (!dateString) return "N/A";

        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "Invalid Date";

          return (
            <span style={{ fontSize: "14px", color: "#666" }}>
              {date.toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              })}
            </span>
          );
        } catch (error) {
          console.error("Date formatting error:", error);
          return "Invalid Date";
        }
      },
      sorter: (a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateA.getTime() - dateB.getTime();
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (description) => (
        <Tooltip title={description || "No description"}>
          <span style={{ fontSize: "14px", color: "#333" }}>
            {description || "No description"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Charges",
      dataIndex: "charges",
      key: "charges",
      width: 100,
      render: (val) => (
        <span style={{ fontSize: "14px", color: "#52c41a", fontWeight: "500" }}>
          ${val ? parseFloat(val).toFixed(2) : "0.00"}
        </span>
      ),
      sorter: (a, b) => {
        const chargesA = parseFloat(a.charges || 0);
        const chargesB = parseFloat(b.charges || 0);
        return chargesA - chargesB;
      },
    },
    {
      title: "Receipt",
      dataIndex: "receipt",
      key: "receipt",
      width: 120,
      render: (filePath, record) => (
        <Space size="small">
          {filePath ? (
            <>
              <Tooltip title="View Receipt">
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handlePdfPreview(filePath, "Receipt", record)}
                  style={{ color: "#206d1a" }}
                />
              </Tooltip>
              <Tooltip title="Download Receipt">
                <Button
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadFile(
                    filePath,
                    `receipt_${record.id}.${filePath.split('.').pop()}`,
                    record.id
                  )}
                  style={{ color: "#206d1a", marginLeft: 4 }}
                />
              </Tooltip>
            </>
          ) : (
            <span style={{ fontSize: "14px", color: "#ccc" }}>No file</span>
          )}
        </Space>
      ),
    },
    {
      title: "Expense Sheet",
      dataIndex: "calculationSheet",
      key: "calculationSheet",
      width: 140,
      render: (filePath, record) => {
  const isPdf = filePath?.toLowerCase().endsWith(".pdf");

  return (
    <Space size="small">
      {filePath ? (
        <>
          {/* VIEW BUTTON (ONLY PDF) */}
          <Tooltip title={isPdf ? "View Receipt" : "Preview not available"}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              disabled={!isPdf}
              onClick={() => handlePdfPreview(filePath, "Receipt")}
              style={{ color: isPdf ? "#1890ff" : "#ccc" }}
            />
          </Tooltip>

          {/* DOWNLOAD BUTTON (ALL FILES) */}
          <Tooltip title="Download Receipt">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() =>
                handleDownloadFile(
                  filePath,
                  `receipt_${record.id}.${filePath.split(".").pop()}`
                )
              }
              style={{ color: "#52c41a" }}
            />
          </Tooltip>
        </>
      ) : (
        <span style={{ color: "#ccc" }}>No file</span>
      )}
    </Space>
  );
}
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ fontSize: "12px" }}>
          {ExpenseManagemnetStatusLabel[status] || "Unknown"}
        </Tag>
      ),
      sorter: (a, b) => a.status - b.status,
      filters: Object.entries(ExpenseManagemnetStatusLabel).map(([key, value]) => ({
        text: value,
        value: parseInt(key),
      })),
    },
    {
      title: "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => {
        const { status } = record;
        const isFinal =
          status === ExpenseManagemnetStatusEnum.Approved ||
          status === ExpenseManagemnetStatusEnum.Rejected;

        return (
          <Space size="small">
            {/* PENDING: can move to Inprocess or Reject */}
            {status === ExpenseManagemnetStatusEnum.Pending && (
              <>
                <Popconfirm
                  title="Move this expense to Inprocess?"
                  onConfirm={() =>
                    handleStatusChange(record, ExpenseManagemnetStatusEnum.Inprocess, "Inprocess")
                  }
                  okText="Yes"
                  cancelText="No"
                >
                  <Button size="small" type="default">
                    Mark Inprocess
                  </Button>
                </Popconfirm>

                <Popconfirm
                  title="Reject this expense?"
                  onConfirm={() =>
                    handleStatusChange(record, ExpenseManagemnetStatusEnum.Rejected, "Rejected")
                  }
                  okText="Yes"
                  cancelText="No"
                >
                  <Button size="small" danger>
                    Reject
                  </Button>
                </Popconfirm>
              </>
            )}

            {/* INPROCESS: can move to Approve or Reject */}
            {status === ExpenseManagemnetStatusEnum.Inprocess && (
              <>
                <Popconfirm
                  title="Approve this expense?"
                  onConfirm={() =>
                    handleStatusChange(record, ExpenseManagemnetStatusEnum.Approved, "Approved")
                  }
                  okText="Yes"
                  cancelText="No"
                >
                  <Button size="small" className="btn-primary">
                    Approve
                  </Button>
                </Popconfirm>

                <Popconfirm
                  title="Reject this expense?"
                  onConfirm={() =>
                    handleStatusChange(record, ExpenseManagemnetStatusEnum.Rejected, "Rejected")
                  }
                  okText="Yes"
                  cancelText="No"
                >
                  <Button size="small" danger>
                    Reject
                  </Button>
                </Popconfirm>
              </>
            )}

            {/* FINAL: no actions */}
            {isFinal && (
              <Button size="small" disabled>
                No Actions
              </Button>
            )}

            {/* Download Report Button */}
            <Tooltip title="Download PDF Report">
              <Button
                type="text"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record)}
                loading={downloadingId === record.id}
                style={{ color: "#206d1a" }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ], [downloadingId]); // Only re-render when downloadingId changes

  // Use useCallback to prevent infinite loops in event handlers
  const handleStatusChange = useCallback(async (record, newStatus, label) => {
    message.loading({ content: `Updating to ${label}...`, key: "status" });
    try {
      await updateExpenseStatus(record.id, newStatus);
      message.success({
        content: `Expense successfully updated to ${label}.`,
        key: "status",
        duration: 2,
      });
      fetchExpenseList(); // refresh
    } catch (err) {
      message.error({
        content:
          err?.response?.data?.message || `Failed to update expense status. Please try again.`,
        key: "status",
      });
    }
  }, [updateExpenseStatus, fetchExpenseList]);

  // Preview Function
const handlePdfPreview = useCallback((filePath, type) => {
  if (!filePath) {
    message.warning("File not available");
    return;
  }

  // Only PDFs can be previewed in iframe
  if (!filePath.toLowerCase().endsWith(".pdf")) {
    message.info("Preview is only available for PDF files. You can download other formats.");
    return;
  }

  const cleanPath = filePath.startsWith("/") ? filePath.slice(1) : filePath;
  const fullUrl = `${BASE_URL}${cleanPath}`;
  setPreviewFile(fullUrl);
  setPreviewType(type);
  setPreviewModalVisible(true);
}, []);

const handleDownloadFile = useCallback((filePath, fileName) => {
  if (!filePath) {
    message.warning("File not available");
    return;
  }

  // Fix URL properly
  const baseUrl = BASE_URL.endsWith("/") ? BASE_URL : BASE_URL + "/";
  const cleanPath = filePath.startsWith("/") ? filePath.slice(1) : filePath;
  const fullUrl = baseUrl + cleanPath;

  // Open in new tab (works without CORS)
  const link = document.createElement("a");
  link.href = fullUrl;
  link.target = "_blank"; // IMPORTANT
  link.download = fileName || cleanPath.split("/").pop();

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}, []);

  // PDF REPORT DOWNLOAD FUNCTION - Based on employee table
  const handleDownload = useCallback(async (expense) => {
    setDownloadingId(expense.id);
    try {
      const jsPDF = await import('jspdf');
      const doc = new jsPDF.default();
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text("Expense Report", 105, 15, { align: "center" });

      // Line styling
      const lineY = 28;
      doc.setLineWidth(0.5);
      doc.setDrawColor(192, 192, 192);
      doc.line(14, lineY, 200, lineY);

      const marginTop = 4;
      
      // Employee information
      const employeeName = expense.employee 
        ? `${expense.employee.firstName} ${expense.employee.lastName}` 
        : "N/A";
      const approverName = expense.approver
        ? `${expense.approver.firstName} ${expense.approver.lastName}` 
        : "N/A";
      const departmentName = expense.department?.name || "N/A";
      const formattedDate = expense.date ? new Date(expense.date).toLocaleDateString() : "N/A";
      const statusLabel = ExpenseManagemnetStatusLabel[expense.status] || "Unknown";

      doc.setFontSize(12);
      doc.text(`Employee: ${employeeName}`, 14, 34 + marginTop);
      doc.text(`Approver: ${approverName}`, 14, 44 + marginTop);
      doc.text(`Requested Date: ${formattedDate}`, 14, 54 + marginTop);
      doc.text(`Department: ${departmentName}`, 14, 64 + marginTop);
      doc.text(`Description: ${expense.description || "N/A"}`, 14, 74 + marginTop);
      doc.text(`Charges: $${expense.charges || 0}`, 14, 84 + marginTop);
      doc.text(`Status: ${statusLabel}`, 14, 94 + marginTop);

      // Add file information
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      let fileY = 104 + marginTop;
      
      if (expense.receipt) {
        doc.text(`Receipt: Available`, 14, fileY);
        fileY += 6;
      }
      
      if (expense.calculationSheet) {
        doc.text(`Expense Sheet: Available`, 14, fileY);
        fileY += 6;
      }

      doc.save(`expense_report_${expense.id || ""}.pdf`);
      message.success("PDF report downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Failed to generate PDF report");
    } finally {
      setDownloadingId(null);
    }
  }, []);

  // Memoize file icon function
  const getFileIcon = useCallback((filePath) => {
    if (!filePath) return <FileOutlined />;
    
    const extension = filePath.split('.').pop().toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <FileExcelOutlined style={{ color: '#52c41a' }} />;
      case 'doc':
      case 'docx':
        return <FileOutlined style={{ color: '#206d1a' }} />;
      default:
        return <FileOutlined />;
    }
  }, []);

  // Memoize status color function
  const getStatusColor = useCallback((status) => {
   const statusColors = {
  [ExpenseManagemnetStatusEnum.Pending]: "gold",       // Warmer, less "emergency" than orange
  [ExpenseManagemnetStatusEnum.Inprocess]: "cyan",     // Fresher and more modern than standard blue
  [ExpenseManagemnetStatusEnum.Approved]: "success",    // Uses the theme's primary success color
  [ExpenseManagemnetStatusEnum.Rejected]: "error",      // Uses the theme's semantic error color
};
    return statusColors[status] || "default";
  }, []);

  // Fetch data on mount
  useEffect(() => {
    fetchExpenseList(); 
  }, [fetchExpenseList]); 

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="container-fluid py-4">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card 
              className="card-professional"
              title={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                
                    <FileOutlined style={{ marginRight: 8 }} />
                    Expense Management List
          
                  <Space>
                    <Search
                      placeholder="Search expenses..."
                      allowClear
                      style={{ width: 250 }}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      prefix={<SearchOutlined />}
                    />
                    <select
                      style={{ width: 200 }}
                      value={filteredStatus}
                      onChange={(e) => setFilteredStatus(e.target.value)}
                      className="form-select"
                    >
                      <option value="all">All Status</option>
                      {Object.entries(ExpenseManagemnetStatusLabel).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </Space>
                </div>
              }
            >
              <Table
                columns={columns}
                dataSource={filteredList}
                rowKey="id"
                loading={loading}
                pagination={{
                  total: filteredList.length,
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                }}
                scroll={{ x: 1400 }}
                size="middle"
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* File Preview Modal */}
      <Modal
        title={`Preview ${previewType}`}
        open={previewModalVisible}
        onCancel={() => {
          setPreviewModalVisible(false);
          setPreviewFile(null);
          setPreviewType("");
        }}
        footer={[
          <Button
            key="download"
            className="btn-primary"
            onClick={() => {
              if (previewFile) {
                const link = document.createElement("a");
                link.href = previewFile;
                link.download = `${previewType}_${previewFile.split("/").pop()}`;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                message.success("File downloaded successfully!");
              }
            }}
          >
            Download
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setPreviewModalVisible(false);
              setPreviewFile(null);
              setPreviewType("");
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {previewFile ? (
          <div style={{ textAlign: "center", padding: "10px" }}>
            <p>Previewing: {previewType}</p>
            <iframe
              src={previewFile}
              style={{ width: "100%", height: "500px", border: "1px solid #d9d9d9", borderRadius: "4px" }}
              title={previewType}
              onError={(e) => {
                console.error("Error loading file in iframe:", e);
                message.error("Failed to load file preview. The file may not be accessible or may not be a previewable format.");
              }}
              
            />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <p>No file to preview</p>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
};

export default ExpenseManagementList;